/**
 * Nmbrs Absence Overview
 *
 * Team-wide absence (verzuim) overview with summary cards and filterable table.
 * Story 3.5: Verzuimoverzicht
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary
 * - Teal #54949E: secondary info
 * - Green #B7D945: no absences
 * - Red #D64545: absences present, open absence badges
 * - Border #E2E8F0: table borders
 */

import { useState, useMemo } from 'react';
import { useAbsenceSummary } from '../hooks/useNmbrsLeave';
import type { AbsenceSummaryItem } from '../hooks/useNmbrsLeave';

// ============================================
// Types
// ============================================

/**
 * The summary endpoint only returns OPEN absences by design (no endDate or endDate >= today).
 * The 'closed' filter will therefore always produce an empty set from this endpoint.
 * To show closed absences, use the per-employee absence endpoint via NmbrsAbsenceCard.
 */
type AbsenceFilter = 'open' | 'all';

// ============================================
// Helper Functions
// ============================================

/**
 * Format a date string to Dutch locale.
 */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ============================================
// Sub-components
// ============================================

/** Loading skeleton for the absence overview. */
function AbsenceSkeleton() {
  return (
    <div className="space-y-6">
      {/* Summary card skeleton */}
      <div className="animate-pulse bg-white rounded-lg border border-[#E2E8F0] p-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 bg-gray-200 rounded-full" />
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-48" />
            <div className="h-4 bg-gray-200 rounded w-32" />
          </div>
        </div>
      </div>

      {/* Table skeleton */}
      <div className="animate-pulse bg-white rounded-lg border border-[#E2E8F0]">
        <div className="p-4 border-b border-[#E2E8F0]">
          <div className="h-5 bg-gray-200 rounded w-40" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="px-4 py-3 border-b border-[#E2E8F0] flex gap-4">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-4 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Error state with retry button. */
function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-8 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-3">
        <svg className="w-6 h-6 text-[#D64545]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <p className="text-sm text-[#1E293B] mb-3">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm font-medium text-white bg-[#2F5A7A] rounded-lg hover:bg-[#254a65] transition-colors"
      >
        Opnieuw proberen
      </button>
    </div>
  );
}

/** Empty state when no absences exist. */
function EmptyState() {
  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-8 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-50 mb-3">
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-sm text-[#1E293B]">Geen verzuimgegevens gevonden</p>
      <p className="text-xs text-gray-500 mt-1">
        Er zijn momenteel geen verzuimregistraties in Nmbrs.
      </p>
    </div>
  );
}

/** Summary card showing absent count. */
function SummaryCard({
  currentlyAbsent,
  totalLinked,
}: {
  currentlyAbsent: number;
  totalLinked: number;
}) {
  const hasAbsent = currentlyAbsent > 0;
  const percentage = totalLinked > 0
    ? Math.round((currentlyAbsent / totalLinked) * 100)
    : 0;

  return (
    <div
      className={`rounded-lg border shadow-sm p-6 ${
        hasAbsent
          ? 'bg-red-50 border-[#D64545]/30'
          : 'bg-green-50 border-[#B7D945]/30'
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`flex items-center justify-center w-14 h-14 rounded-full ${
            hasAbsent ? 'bg-[#D64545]/10' : 'bg-[#B7D945]/20'
          }`}
        >
          <span
            className={`text-2xl font-bold ${
              hasAbsent ? 'text-[#D64545]' : 'text-green-700'
            }`}
          >
            {currentlyAbsent}
          </span>
        </div>
        <div>
          <h3 className="text-base font-semibold text-[#1E293B]">
            Momenteel afwezig: {currentlyAbsent} van {totalLinked} medewerkers
          </h3>
          <p className={`text-sm ${hasAbsent ? 'text-[#D64545]' : 'text-green-700'}`}>
            {percentage}% verzuimpercentage
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function NmbrsAbsenceOverview() {
  const [filter, setFilter] = useState<AbsenceFilter>('all');
  const { data, isLoading, error, refetch } = useAbsenceSummary(filter === 'all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const summary = data?.data;

  // 'all' fetches all absences (includeAll=true), 'open' fetches only open (includeAll=false).
  // The backend handles the filtering; frontend just applies type filter and sorts.
  const filteredAbsences = useMemo(() => {
    if (!summary) return [];

    let absences = summary.absences;

    // Apply type filter
    if (typeFilter !== 'all') {
      absences = absences.filter((a) => a.type === typeFilter);
    }

    // Sort: open absences first (no endDate), then by start date (newest first)
    return [...absences].sort((a, b) => {
      if (!a.endDate && b.endDate) return -1;
      if (a.endDate && !b.endDate) return 1;
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });
  }, [summary, typeFilter]);

  // Collect unique absence types for the type filter dropdown
  const absenceTypes = useMemo(() => {
    if (!summary) return [];
    return [...new Set(summary.absences.map((a) => a.type))].sort();
  }, [summary]);

  if (isLoading) return <AbsenceSkeleton />;

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
    return <ErrorState message={errorMessage} onRetry={() => refetch()} />;
  }

  if (!summary) return <EmptyState />;

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <SummaryCard
        currentlyAbsent={summary.currentlyAbsent}
        totalLinked={summary.totalLinked}
      />

      {/* Absence Table */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden">
        {/* Table Header with Filters */}
        <div className="px-4 py-3 border-b border-[#E2E8F0] flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-[#2F5A7A]">
            Verzuimoverzicht
          </h3>
          <div className="flex items-center gap-3">
            {/* Open/All toggle */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Status:</span>
              <div className="flex rounded-lg border border-[#E2E8F0] overflow-hidden">
                {([
                  { key: 'all' as const, label: 'Alle' },
                  { key: 'open' as const, label: 'Open' },
                ] as const).map(({ key, label }) => (
                  <button
                    key={key}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      filter === key
                        ? 'bg-[#2F5A7A] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Type filter dropdown */}
            {absenceTypes.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Type:</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-2 py-1 text-xs border border-[#E2E8F0] rounded-lg bg-white text-gray-700 focus:ring-1 focus:ring-[#2F5A7A] focus:border-[#2F5A7A]"
                >
                  <option value="all">Alle types</option>
                  {absenceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {filteredAbsences.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500">
              {filter === 'open'
                ? 'Geen open verzuimmeldingen zonder einddatum.'
                : typeFilter !== 'all'
                  ? `Geen verzuimmeldingen van type "${typeFilter}" gevonden.`
                  : 'Geen actieve verzuimmeldingen gevonden.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E2E8F0]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Medewerker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Startdatum
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Einddatum
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Percentage
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Verzuimdagen
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Contracturen/week
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {filteredAbsences.map((absence, index) => (
                  <AbsenceRow key={`${absence.employeeId}-${absence.startDate}-${index}`} absence={absence} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Single row in the absence table. */
function AbsenceRow({ absence }: { absence: AbsenceSummaryItem }) {
  const isOpen = !absence.endDate;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-[#1E293B]">
          {absence.employeeName}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-600">
          {formatDate(absence.startDate)}
        </span>
      </td>
      <td className="px-4 py-3">
        {isOpen ? (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-[#D64545]/10 text-[#D64545]">
            Heden
          </span>
        ) : (
          <span className="text-sm text-gray-600">
            {formatDate(absence.endDate!)}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm text-gray-600">
          {absence.percentage}%
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-medium text-[#1E293B]">
          {absence.absenceDays != null ? absence.absenceDays : '–'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm text-gray-600">
          {absence.contractHours != null ? `${absence.contractHours}u` : '–'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-600">
          {absence.type}
        </span>
      </td>
    </tr>
  );
}
